import { apiClient } from '../api/ApiClient';
import {
  ManualImportResultDto,
  RedditCrawlStatusDto
} from '@/application/dto/AdminOperationsDtos';
import { QueueHealthHistoryDto } from '@/domain/entities/Region';

/**
 * Admin Operations Repository
 * Infrastructure layer - handles HTTP requests to admin operations endpoints
 *
 * All endpoints require Admin role authentication.
 */
export class AdminOperationsRepository {
  /**
   * Trigger manual OSM import for a SubArea or all SubAreas in a region
   * POST /api/admin/operations/osm/import
   *
   * @param subAreaId - GUID of the SubArea to import data for (single SubArea), null for all SubAreas
   * @param regionId - GUID of the Region to import all SubAreas for (multi-SubArea import), optional
   * @returns Promise<ManualImportResultDto> - Import result with statistics
   */
  async triggerOsmImport(
    subAreaId: string | null,
    regionId?: string
  ): Promise<ManualImportResultDto> {
    return apiClient.post<ManualImportResultDto>(
      '/api/admin/operations/osm/import',
      {
        subAreaId,
        regionId
      }
    );
  }

  /**
   * Start Reddit crawl manually
   * POST /api/admin/operations/reddit/crawl/start
   *
   * Note: Placeholder for Sessions 005/006
   * @returns Promise<void>
   */
  async startRedditCrawl(): Promise<void> {
    return apiClient.post<void>(
      `/api/admin/operations/reddit/crawl/start`
    );
  }

  /**
   * Stop Reddit crawl manually
   * POST /api/admin/operations/reddit/crawl/stop
   *
   * Note: Placeholder for Sessions 005/006
   * @returns Promise<void>
   */
  async stopRedditCrawl(): Promise<void> {
    return apiClient.post<void>(
      `/api/admin/operations/reddit/crawl/stop`
    );
  }

  /**
   * Get current Reddit crawl status
   * GET /api/admin/operations/reddit/crawl/status
   *
   * Note: Placeholder for Sessions 005/006
   * @returns Promise<RedditCrawlStatusDto> - Current crawl status
   */
  async getRedditCrawlStatus(): Promise<RedditCrawlStatusDto> {
    return apiClient.get<RedditCrawlStatusDto>(
      `/api/admin/operations/reddit/crawl/status`
    );
  }

  /**
   * Get overall crawler status with health metrics
   * GET /api/admin/operations/reddit/crawl/status
   *
   * @returns Promise with crawler status and statistics
   */
  async getCrawlStatus(): Promise<unknown> {
    return apiClient.get<unknown>('/api/admin/operations/reddit/crawl/status');
  }

  /**
   * Get recent crawl history records with pagination and filtering
   * GET /api/admin/operations/reddit/crawl/history
   * Sprint 027 SESSION_358: Added pagination (page, pageSize) and subreddit filter.
   *
   * @param hours - Number of hours to look back
   * @param maxResults - Maximum number of results (DEPRECATED - use pageSize)
   * @param page - Page number for pagination
   * @param pageSize - Number of items per page
   * @param subredditId - Optional filter by subreddit ID
   * @returns Promise with paginated crawl history records
   */
  async getCrawlHistory(
    hours: number,
    maxResults: number,
    page: number = 1,
    pageSize: number = 50,
    subredditId?: string
  ): Promise<unknown> {
    let url = `/api/admin/operations/reddit/crawl/history?hours=${hours}&maxResults=${maxResults}&page=${page}&pageSize=${pageSize}`;
    if (subredditId) {
      url += `&subredditId=${subredditId}`;
    }
    return apiClient.get<unknown>(url);
  }

  /**
   * SESSION_369: Get all regions for dropdown selection
   * GET /api/admin/operations/regions
   *
   * @returns Promise with list of regions with their Reddit source counts
   */
  async getRegions(): Promise<unknown> {
    return apiClient.get<unknown>('/api/admin/operations/regions');
  }

  /**
   * SESSION_369: Get Reddit sources for a specific region
   * GET /api/admin/operations/reddit/sources
   *
   * @param regionId - Region ID to filter by
   * @param isActive - Optional filter by active/inactive status
   * @returns Promise with list of Reddit sources for the region
   */
  async getRedditSourcesForRegion(
    regionId: string,
    isActive?: boolean
  ): Promise<unknown> {
    let url = `/api/admin/operations/reddit/sources?regionId=${regionId}`;
    if (isActive !== undefined) {
      url += `&isActive=${isActive}`;
    }
    return apiClient.get<unknown>(url);
  }

  /**
   * S_031_C_004: Get queue health history for a region
   * GET /api/admin/operations/regions/{regionId}/queue-health/history
   *
   * @param regionId - Region ID to get history for
   * @param hours - Number of hours to look back (default: 24, options: 1, 6, 24, 168 for 7 days)
   * @returns Promise with queue health history including data points
   */
  async getQueueHealthHistory(
    regionId: string,
    hours: number = 24
  ): Promise<QueueHealthHistoryDto> {
    return apiClient.get<QueueHealthHistoryDto>(
      `/api/admin/operations/regions/${regionId}/queue-health/history?hours=${hours}`
    );
  }
}

/**
 * Default repository instance
 */
export const adminOperationsRepository = new AdminOperationsRepository();
